import { ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import ProjectHistory from '@ors/components/manage/Blocks/ProjectsListing/ProjectView/ProjectHistory.tsx'
import SectionErrorIndicator from '@ors/components/ui/SectionTab/SectionErrorIndicator.tsx'
import CustomAlert from '@ors/components/theme/Alerts/CustomAlert.tsx'
import ProjectsDataContext from '@ors/contexts/Projects/ProjectsDataContext.tsx'
import PermissionsContext from '@ors/contexts/PermissionsContext.tsx'
import ProjectIdentifiersSection from './ProjectIdentifiersSection.tsx'
import ProjectCrossCuttingFields from './ProjectCrossCuttingFields'
import ProjectSpecificInfoSection from './ProjectSpecificInfoSection.tsx'
import ProjectImpact from './ProjectImpact.tsx'
import ProjectDocumentation from '../ProjectView/ProjectDocumentation.tsx'
import ProjectApprovalFields from './ProjectApprovalFields.tsx'
import ProjectUmbrellaProjectDetails from '../ProjectView/ProjectUmbrellaProjectsDetails.tsx'
import ProjectAPRData from '../ProjectView/ProjectAPRData.tsx'
import ProjectsInlineMessage from './ProjectsInlineMessage.tsx'
import ProjectDelete from './ProjectDelete.tsx'
import { DisabledAlert, ErrorsList, LoadingTab } from '../HelperComponents.tsx'
import useGetProjectFieldsOpts from '../hooks/useGetProjectFieldsOpts.tsx'
import { useGetProjectsAPRData } from '../hooks/useGetProjectsAPRData.ts'
import { useGetMetaProjectDetails } from '../UpdateMyaData/hooks.ts'
import { MAX_FILE_SIZE, projectPhaseOutFields } from '../constants.ts'
import {
  ProjectFile,
  ProjectSpecificFields,
  ProjectTypeApi,
  ProjectDataProps,
  ProjectFiles,
  TrancheErrors,
  RelatedProjectsSectionType,
  BpDataProps,
  FileMetaDataProps,
  OdsOdpFields,
} from '../interfaces.ts'
import {
  canGoToSecondStep,
  checkInvalidValue,
  formatErrors,
  getCrossCuttingErrors,
  getFieldLabel,
  getProjIdentifiersErrors,
  getSectionFields,
  getSpecificFieldsErrors,
  getHasNoFiles,
  hasSectionErrors,
  hasFields,
  getApprovalErrors,
  getAgencyErrorType,
  getPostExcomApprovalErrors,
  getFieldData,
  formatOptions,
  getOdsOdpFields,
  getPostExcomMeetingErrors,
  isOtherOdsReplacement,
  formatMetaprojectData,
  getMpErrors,
} from '../utils.ts'
import { useStore } from '@ors/store.tsx'

import { find, has, isEmpty, isEqual, map, mapKeys, pick } from 'lodash'
import { Tabs, Tab, Typography } from '@mui/material'
import { useParams } from 'wouter'

export const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div className="mb-4 text-xl uppercase tracking-[1px] text-typography-sectionTitle">
    {children}
  </div>
)

const ProjectsCreate = ({
  projectData,
  setProjectData,
  specificFields,
  mode,
  postExComUpdate = false,
  approval = false,
  impact = false,
  files,
  projectFiles,
  errors,
  project,
  fileErrors,
  trancheErrors,
  getTrancheErrors,
  relatedProjects,
  approvalFields = [],
  specificFieldsLoaded,
  loadedFiles,
  bpData,
  onBpDataChange,
  filesMetaData,
  setFilesMetaData,
  metaProjectId,
  setMetaProjectId,
  setRefetchRelatedProjects,
  shouldValidateTotalFund,
  setErrors,
  ...rest
}: ProjectDataProps &
  ProjectFiles &
  TrancheErrors &
  FileMetaDataProps & {
    specificFields: ProjectSpecificFields[]
    mode: string
    postExComUpdate?: boolean
    approval?: boolean
    impact?: boolean
    errors: { [key: string]: [] }
    fileErrors: string
    project?: ProjectTypeApi
    projectFiles?: ProjectFile[]
    relatedProjects: RelatedProjectsSectionType[]
    approvalFields?: ProjectSpecificFields[]
    specificFieldsLoaded: boolean
    loadedFiles?: boolean
    bpData: BpDataProps
    onBpDataChange: (bpData: BpDataProps) => void
    metaProjectId?: number | null
    setMetaProjectId?: (id: number | null) => void
    setRefetchRelatedProjects?: (refetch: boolean) => void
    shouldValidateTotalFund: boolean
    setErrors: (value: { [key: string]: [] }) => void
  }) => {
  const { project_id } = useParams<Record<string, string>>()

  const userSlice = useStore((state) => state.user)
  const { agency_id } = userSlice.data

  const {
    projIdentifiers,
    crossCuttingFields,
    projectSpecificFields,
    approvalFields: approvalData,
  } = projectData ?? {}
  const { project_type, sector } = crossCuttingFields

  const fieldsOpts = useGetProjectFieldsOpts(projectData, setProjectData, mode)
  const { results: aprData } = useGetProjectsAPRData(project_id, mode)

  const isEditMode = project && mode === 'edit'
  const isApprovalTabAvailable = isEditMode && project.version >= 3
  const shouldValidateRequiredFields = !(
    mode === 'edit' && project?.submission_status === 'Approved'
  )

  const canLinkToBp = canGoToSecondStep(
    projIdentifiers,
    agency_id,
    shouldValidateRequiredFields,
  )

  const [currentStep, setCurrentStep] = useState<number>(canLinkToBp ? 5 : 0)
  const [currentTab, setCurrentTab] = useState<number>(
    impact ? 3 : approval ? 5 : 0,
  )

  const areFieldsDisabled = !canLinkToBp || currentStep < 1
  const areNextSectionsDisabled = areFieldsDisabled || bpData.bpDataLoading
  const areProjectSpecificTabsDisabled =
    areNextSectionsDisabled || !project_type || !sector

  const [overviewFields, substanceDetailsFields, impactFields] = [
    getSectionFields(specificFields, 'Header'),
    getSectionFields(specificFields, 'Substance Details'),
    getSectionFields(specificFields, 'Impact'),
  ]
  const odsOdpFields = getOdsOdpFields(substanceDetailsFields)
  const odsDisplayField = getFieldData(odsOdpFields, 'ods_display_name')

  const { warnings } = useStore((state) => state.projectWarnings)
  const { projectFields, viewableFields, editableFields } = useStore(
    (state) => state.projectFields,
  )

  const groupField = getFieldData(overviewFields, 'group')
  const specificFieldsIdentifiers = 'projectSpecificFields'
  const specificFieldsData = projectData[specificFieldsIdentifiers] || []

  const meta_project_id = project && mode === 'edit' ? metaProjectId : null
  const submissionStatus =
    project && mode === 'edit' ? project.submission_status : null

  const { data: metaprojectData, refresh: refreshMetaProjectDetails } =
    useGetMetaProjectDetails(
      meta_project_id,
      mode,
      projIdentifiers,
      submissionStatus,
    )

  const {
    mpData,
    setMpData,
    defaultMpErrors,
    setDefaultMpErrors,
    allMpErrors,
    setAllMpErrors,
  } = useStore((state) => state.mpData)

  useEffect(() => {
    const formattedMpdata = formatMetaprojectData(metaprojectData, ['end_date'])
    setMpData(formattedMpdata)
  }, [metaprojectData])

  const { inlineMessage, setInlineMessage } = useStore(
    (state) => state.inlineMessage,
  )

  useEffect(() => {
    setInlineMessage(null)
  }, [])

  useEffect(() => {
    if (groupField) {
      const groupOptions = formatOptions(groupField, specificFieldsData)

      const validData = find(
        groupOptions,
        (option) => option.id === specificFieldsData.group,
      )

      if (!validData) {
        setProjectData((prevData) => ({
          ...prevData,
          [specificFieldsIdentifiers]: {
            ...prevData[specificFieldsIdentifiers],
            group: null,
          },
        }))
      }
    }
  }, [groupField])

  const isTabDisabled = areNextSectionsDisabled || bpData.bpDataLoading

  const isCrossCuttingTabDisabled =
    isTabDisabled || !hasFields(projectFields, viewableFields, 'Cross-Cutting')

  const hasNoSpecificInfoFields =
    overviewFields.length < 1 && substanceDetailsFields.length < 1
  const isSpecificInfoTabDisabled =
    !specificFieldsLoaded ||
    bpData.bpDataLoading ||
    areProjectSpecificTabsDisabled ||
    hasNoSpecificInfoFields ||
    (!hasFields(projectFields, viewableFields, 'Header') &&
      !hasFields(projectFields, viewableFields, 'Substance Details'))

  const isImpactTabDisabled =
    !specificFieldsLoaded ||
    bpData.bpDataLoading ||
    areProjectSpecificTabsDisabled ||
    impactFields.length < 1 ||
    !hasFields(projectFields, viewableFields, 'Impact')

  useEffect(() => {
    if (
      impact &&
      specificFieldsLoaded &&
      (impactFields.length < 1 ||
        !hasFields(projectFields, viewableFields, 'Impact'))
    ) {
      setCurrentTab(0)
    }
  }, [specificFieldsLoaded])

  const isApprovalTabDisabled =
    areNextSectionsDisabled ||
    approvalFields.length < 1 ||
    !hasFields(projectFields, viewableFields, 'Approval')

  const projIdentifiersErrors = useMemo(
    () =>
      getProjIdentifiersErrors(
        projIdentifiers,
        errors,
        shouldValidateRequiredFields,
      ),
    [projIdentifiers, errors],
  )
  const agencyErrorType = getAgencyErrorType(projIdentifiers, agency_id)

  const hasPostExcomMeetingErrors = getPostExcomMeetingErrors(projIdentifiers)
  const postExcomMeetingErrors = {
    post_excom_meeting:
      postExComUpdate &&
      !!projIdentifiers.post_excom_meeting &&
      hasPostExcomMeetingErrors
        ? ['Cannot be earlier than the original approval meeting.']
        : [],
  }

  const bpErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors ?? {}).filter(([key]) => key === 'bp_activity'),
      ),
    [errors],
  )

  const crossCuttingErrors = useMemo(
    () =>
      getCrossCuttingErrors(
        crossCuttingFields,
        errors,
        mode,
        mode === 'edit' ? project : undefined,
        true,
        shouldValidateTotalFund,
        shouldValidateRequiredFields,
      ),
    [crossCuttingFields, errors, mode, shouldValidateTotalFund],
  )

  const approvalErrors = useMemo(() => {
    const approvalCrossCuttingErrors = pick(crossCuttingErrors, [
      'total_fund',
      'support_cost_psc',
    ])

    return mode === 'edit' && project?.submission_status === 'Recommended'
      ? {
          ...getApprovalErrors(
            approvalData,
            crossCuttingFields,
            approvalFields,
            errors,
            project,
          ),
          ...approvalCrossCuttingErrors,
        }
      : postExComUpdate
        ? getPostExcomApprovalErrors(
            approvalData,
            approvalFields,
            errors,
            project,
          )
        : {}
  }, [
    approvalData,
    crossCuttingFields,
    approvalFields,
    errors,
    crossCuttingErrors,
  ])

  const myaDefaultErrors = useMemo(
    () => getMpErrors(mpData, metaprojectData, {}, project, mode),
    [mpData, metaprojectData],
  )

  const myaAllErrors = useMemo(
    () => getMpErrors(mpData, metaprojectData, errors, project, mode),
    [mpData, metaprojectData, errors],
  )

  useEffect(() => {
    if (!isEqual(myaDefaultErrors, defaultMpErrors)) {
      setDefaultMpErrors(myaDefaultErrors)
    }
  }, [myaDefaultErrors, defaultMpErrors])

  useEffect(() => {
    if (!isEqual(myaAllErrors, allMpErrors)) {
      setAllMpErrors(myaAllErrors)
    }
  }, [myaAllErrors, allMpErrors])

  const { canEditApprovedProjects } = useContext(PermissionsContext)
  const { altTechs } = useContext(ProjectsDataContext)

  const hasV3EditPermissions =
    !!project && mode === 'edit' && canEditApprovedProjects
  const editableByAdmin = ['Approved', 'Withdrawn', 'Not approved'].includes(
    project?.submission_status ?? '',
  )
  const isV3ProjectEditable =
    hasV3EditPermissions &&
    (editableByAdmin || project.submission_status === 'Recommended')
  const disableV3Edit =
    !!project &&
    mode === 'edit' &&
    !postExComUpdate &&
    !project.editable &&
    project.editable_for_actual_fields

  const hasComponents =
    !!project &&
    project.component &&
    project.component.original_project_id === project.id

  const specificFieldsErrors = useMemo(
    () =>
      getSpecificFieldsErrors(
        projectSpecificFields,
        specificFields,
        errors,
        mode,
        canEditApprovedProjects,
        shouldValidateRequiredFields,
        project,
      ),
    [projectSpecificFields, specificFields, errors, mode, project],
  )

  const overviewErrors = specificFieldsErrors['Header'] || {}
  const substanceDetailsErrors = specificFieldsErrors['Substance Details'] || {}
  const allImpactErrors = specificFieldsErrors['Impact'] || {}

  const isActualFieldEmpty = ([key, value]: [string, string[]]) =>
    key.includes('- actual') && value?.[0]?.includes('not completed')

  const impactErrors = Object.fromEntries(
    Object.entries(allImpactErrors).filter(
      (error) => !isActualFieldEmpty(error),
    ),
  )

  const { errorText, isError } = trancheErrors || {}

  const fieldsForValidation = map(odsOdpFields, 'write_field_name').filter(
    (field) => !projectPhaseOutFields.includes(field),
  )

  const odsOdpData = projectSpecificFields?.ods_odp ?? []

  const errorMessageExtension =
    project?.submission_status === 'Draft' ? ' for submission' : ''

  const odsOpdDataErrors =
    odsOdpFields.length > 0
      ? map(odsOdpData, (odsOdp) => {
          const errors = map(fieldsForValidation, (field) => {
            const formattedField =
              field !== 'ods_replacement_text' ||
              isOtherOdsReplacement(altTechs, odsOdp['ods_replacement'])
                ? field
                : 'ods_replacement'

            return mode === 'edit' &&
              shouldValidateRequiredFields &&
              checkInvalidValue(odsOdp[formattedField as keyof OdsOdpFields])
              ? [field, [`This field is required${errorMessageExtension}.`]]
              : null
          }).filter(Boolean) as [string, string[]][]

          return Object.fromEntries(errors)
        })
      : []

  const formattedOdsOdp = map(odsOpdDataErrors, (error, index) => ({
    ...error,
    ...(((errors?.ods_odp ?? [])[index] as Record<string, []>) || {}),
  }))

  const filteredOdsOdpErrors = map(formattedOdsOdp, (odp, index) =>
    !isEmpty(odp) ? { ...odp, id: index } : { ...odp },
  ).filter((odp) => !isEmpty(odp) && !has(odp, 'non_field_errors'))

  const formatFieldName = (field: string) =>
    ['ods_substance_id', 'ods_blend_id'].includes(field)
      ? 'ods_display_name'
      : field === 'ods_replacement'
        ? 'ods_replacement_text'
        : field

  const formattedOdsOdpErrors = map(
    filteredOdsOdpErrors,
    ({ id, ...fields }) => {
      const substanceNo = Number(id) + 1

      const fieldLabels = Object.entries(
        fields as Record<string, string[]>,
      ).filter(([_, errors]) => Array.isArray(errors) && errors.length > 0)

      if (fieldLabels.length === 0) return null

      const missingFields = fieldLabels
        .filter(
          ([_, errors]) =>
            errors[0] === `This field is required${errorMessageExtension}.`,
        )
        .map(([field]) => getFieldLabel(specificFields, formatFieldName(field)))

      const invalidFields = fieldLabels
        .filter(
          ([_, errors]) =>
            errors[0] !== `This field is required${errorMessageExtension}.`,
        )
        .map(([field]) => getFieldLabel(specificFields, formatFieldName(field)))

      const messages = [
        missingFields.length > 0
          ? `${missingFields.join(', ')}: ${
              missingFields.length > 1 ? 'These fields are' : 'This field is'
            } required${errorMessageExtension}.`
          : null,
        invalidFields.length > 0
          ? `${invalidFields.join(', ')}: ${
              invalidFields.length > 1 ? 'These fields are' : 'This field is'
            } not valid.`
          : null,
      ].filter(Boolean)

      const odsOdpType = odsDisplayField
        ? `Substance ${substanceNo}`
        : 'Phase out'

      return { message: `${odsOdpType} - ` + messages.join(' ') }
    },
  ).filter(Boolean)

  const odsOdpErrors = map(
    formattedOdsOdp as { [key: string]: [] }[],
    (error) =>
      mapKeys(error, (_, key) =>
        getFieldLabel(specificFields, formatFieldName(key)),
      ),
  )

  const hasNoFiles =
    loadedFiles &&
    mode === 'edit' &&
    project?.submission_status !== 'Withdrawn' &&
    (project?.submission_status !== 'Draft' || project?.version === 1) &&
    (project?.version ?? 0) < 3 &&
    (!project?.component ||
      project?.id === project?.component.original_project_id) &&
    getHasNoFiles(parseInt(project_id), files, projectFiles)

  const missingFileTypeErrors =
    mode === 'add' || loadedFiles
      ? map(filesMetaData, ({ type, size }, index) => {
          const typeErrorMessage = !type ? 'Type is required.' : ''
          const sizeErrorMessage =
            (size ?? 0) > MAX_FILE_SIZE ? 'File size exceeds 20 MB.' : ''

          return typeErrorMessage || sizeErrorMessage
            ? {
                id: index,
                message: `Attachment ${Number(index) + 1} - ${typeErrorMessage} ${sizeErrorMessage}`,
              }
            : null
        }).filter(Boolean)
      : []

  const steps = [
    {
      id: 'project-identifiers',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Identifiers</div>
          {bpData.bpDataLoading
            ? LoadingTab
            : (hasSectionErrors(projIdentifiersErrors) ||
                (shouldValidateRequiredFields && !!agencyErrorType) ||
                (postExComUpdate &&
                  !(
                    projIdentifiers.post_excom_meeting &&
                    projIdentifiers.post_excom_decision
                  )) ||
                hasSectionErrors(postExcomMeetingErrors) ||
                hasSectionErrors(bpErrors)) && (
                <SectionErrorIndicator errors={[]} />
              )}
        </div>
      ),
      disabled: !hasFields(projectFields, viewableFields, 'Identifiers'),
      component: (
        <ProjectIdentifiersSection
          {...{
            projectData,
            setProjectData,
            setCurrentStep,
            setCurrentTab,
            mode,
            project,
            postExComUpdate,
            isV3ProjectEditable,
            specificFieldsLoaded,
            bpData,
            onBpDataChange,
            bpErrors,
          }}
          areNextSectionsDisabled={areFieldsDisabled}
          isNextBtnEnabled={canLinkToBp}
          errors={{ ...projIdentifiersErrors, ...postExcomMeetingErrors }}
        />
      ),
      errors: [
        ...formatErrors({
          ...projIdentifiersErrors,
          ...postExcomMeetingErrors,
          ...bpErrors,
        }),
        ...(shouldValidateRequiredFields && !!agencyErrorType
          ? [
              {
                message:
                  agencyErrorType === 'no_valid_agency'
                    ? 'At least one agency field must include your own agency.'
                    : agencyErrorType === 'similar_agencies'
                      ? 'Agency and lead agency cannot be similar when submitting on behalf of a cooperating agency.'
                      : 'Agency and lead agency cannot be different unless submitting on behalf of a cooperating agency.',
              },
            ]
          : []),
      ],
    },
    {
      id: 'project-cross-cutting-section',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Cross-Cutting</div>
          {hasSectionErrors(crossCuttingErrors) &&
            (isCrossCuttingTabDisabled ? (
              DisabledAlert
            ) : (
              <SectionErrorIndicator errors={[]} />
            ))}
        </div>
      ),
      disabled: isCrossCuttingTabDisabled,
      component: (
        <ProjectCrossCuttingFields
          {...{
            projectData,
            setProjectData,
            project,
            setCurrentTab,
            fieldsOpts,
            specificFieldsLoaded,
            postExComUpdate,
            isV3ProjectEditable,
            mode,
          }}
          nextStep={
            !isSpecificInfoTabDisabled ? 3 : !isImpactTabDisabled ? 4 : 5
          }
          errors={crossCuttingErrors}
        />
      ),
      errors: formatErrors(crossCuttingErrors),
    },
    {
      id: 'project-specific-info-section',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Specific Information</div>
          {!specificFieldsLoaded
            ? LoadingTab
            : !hasNoSpecificInfoFields &&
              (hasSectionErrors(overviewErrors) ||
                hasSectionErrors(substanceDetailsErrors) ||
                formattedOdsOdpErrors.length > 0 ||
                errorText ||
                (mode === 'edit' &&
                  shouldValidateRequiredFields &&
                  odsOdpFields.length > 0 &&
                  odsOdpData.length === 0)) &&
              (isSpecificInfoTabDisabled ? (
                DisabledAlert
              ) : (
                <SectionErrorIndicator errors={[]} />
              ))}
        </div>
      ),
      disabled: isSpecificInfoTabDisabled,
      component: (
        <ProjectSpecificInfoSection
          {...{
            projectData,
            setProjectData,
            overviewFields,
            substanceDetailsFields,
            overviewErrors,
            substanceDetailsErrors,
            odsOdpErrors,
            trancheErrors,
            getTrancheErrors,
            setCurrentTab,
            disableV3Edit,
          }}
          nextStep={!isImpactTabDisabled ? 4 : 5}
        />
      ),
      errors: [
        ...formatErrors({
          ...overviewErrors,
          ...substanceDetailsErrors,
        }),
        ...(mode === 'edit' &&
        shouldValidateRequiredFields &&
        odsOdpFields.length > 0 &&
        odsOdpData.length === 0
          ? [
              {
                message: `At least a substance must be provided${errorMessageExtension}.`,
              },
            ]
          : []),
        ...formattedOdsOdpErrors,
        ...(errorText && isError ? [{ message: errorText }] : []),
      ],
    },
    {
      id: 'project-impact-section',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Impact</div>
          {!specificFieldsLoaded
            ? LoadingTab
            : impactFields.length >= 1 &&
              hasSectionErrors(impactErrors) &&
              (isImpactTabDisabled ? (
                DisabledAlert
              ) : (
                <SectionErrorIndicator errors={[]} />
              ))}
        </div>
      ),
      disabled: isImpactTabDisabled,
      component: (
        <ProjectImpact
          sectionFields={impactFields}
          errors={impactErrors}
          nextStep={!isSpecificInfoTabDisabled ? 3 : 2}
          {...{ projectData, setProjectData, setCurrentTab }}
        />
      ),
      errors: formatErrors(impactErrors),
    },
    {
      id: 'project-documentation-section',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Attachments</div>
          {mode !== 'add' && !loadedFiles ? (
            LoadingTab
          ) : fileErrors || hasNoFiles || missingFileTypeErrors.length > 0 ? (
            areNextSectionsDisabled || bpData.bpDataLoading ? (
              DisabledAlert
            ) : (
              <SectionErrorIndicator errors={[]} />
            )
          ) : null}
        </div>
      ),
      disabled: areNextSectionsDisabled,
      component: (
        <ProjectDocumentation
          {...{
            projectFiles,
            files,
            mode,
            project,
            loadedFiles,
            setCurrentTab,
            filesMetaData,
            setFilesMetaData,
            disableV3Edit,
          }}
          prevStep={
            !isImpactTabDisabled ? 4 : !isSpecificInfoTabDisabled ? 3 : 2
          }
          isNextButtonDisabled={
            isApprovalTabAvailable ? isApprovalTabDisabled : false
          }
          errors={missingFileTypeErrors}
          {...rest}
        />
      ),
      errors: [
        ...(fileErrors ? [{ message: fileErrors }] : []),
        ...(hasNoFiles
          ? [
              {
                message: `At least one file must be attached to this version${errorMessageExtension}.`,
              },
            ]
          : []),
        ...missingFileTypeErrors,
      ],
    },
    ...(isApprovalTabAvailable
      ? [
          {
            id: 'project-approval-section',
            label: (
              <div className="relative flex items-center justify-between gap-x-2">
                <div className="leading-tight">Approval</div>
                {approvalFields.length === 0
                  ? LoadingTab
                  : hasSectionErrors(approvalErrors) &&
                    (isApprovalTabDisabled || bpData.bpDataLoading ? (
                      DisabledAlert
                    ) : (
                      <SectionErrorIndicator errors={[]} />
                    ))}
              </div>
            ),
            disabled: isApprovalTabDisabled,
            component: (
              <ProjectApprovalFields
                sectionFields={approvalFields as ProjectSpecificFields[]}
                {...{
                  projectData,
                  setProjectData,
                  project,
                  setCurrentTab,
                }}
                errors={approvalErrors}
              />
            ),
            errors: formatErrors(approvalErrors),
          },
        ]
      : []),
    {
      id: 'project-related-projects-section',
      label: (
        <div className="relative flex items-center justify-between gap-x-2">
          <div className="leading-tight">Umbrella project details</div>
          {hasSectionErrors(myaAllErrors) &&
            (isTabDisabled || bpData.bpDataLoading ? (
              DisabledAlert
            ) : (
              <SectionErrorIndicator errors={[]} />
            ))}
        </div>
      ),
      disabled: areNextSectionsDisabled,
      component: (
        <ProjectUmbrellaProjectDetails
          canDisassociate={postExComUpdate}
          {...{
            project,
            relatedProjects,
            metaProjectId,
            setMetaProjectId,
            setRefetchRelatedProjects,
            setCurrentTab,
            metaprojectData,
            refreshMetaProjectDetails,
            mode,
            setErrors,
          }}
          isMya={projIdentifiers.category === 'MYA'}
          isPrevButtonDisabled={
            isApprovalTabAvailable ? isApprovalTabDisabled : false
          }
        />
      ),
    },
    ...(isEditMode &&
    project.submission_status === 'Approved' &&
    aprData.length > 0
      ? [
          {
            id: 'project-apr-section',
            label: (
              <div className="relative flex items-center justify-between gap-x-2">
                <div className="leading-tight">Progress reports</div>
              </div>
            ),
            disabled: areNextSectionsDisabled,
            component: <ProjectAPRData {...{ aprData, setCurrentTab }} />,
          },
        ]
      : []),
    ...(isEditMode
      ? [
          {
            id: 'project-history-section',
            label: (
              <div className="relative flex items-center justify-between gap-x-2">
                <div className="leading-tight">History</div>
              </div>
            ),
            disabled: areNextSectionsDisabled,
            component: <ProjectHistory {...{ project, setCurrentTab }} />,
          },
        ]
      : []),
  ]

  return (
    <>
      <div className="flex items-center justify-between">
        <Tabs
          aria-label="create-project"
          value={currentTab}
          className="sectionsTabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{
            className: 'h-0',
            style: { transitionDuration: '150ms' },
          }}
          onChange={(_, newValue) => {
            setCurrentTab(newValue)
          }}
        >
          {steps.map(({ id, label, disabled }) => (
            <Tab
              key={id}
              id={id}
              aria-controls={id}
              label={label}
              disabled={disabled}
              classes={{
                disabled: 'text-gray-300',
              }}
            />
          ))}
        </Tabs>
        {mode === 'edit' &&
          project?.submission_status === 'Draft' &&
          project?.editable && (
            <ProjectDelete {...{ project, hasComponents }} />
          )}
      </div>
      <div className="relative rounded-b-lg rounded-r-lg border border-solid border-primary p-6">
        {steps
          .filter((_, index) => index === currentTab)
          .map(({ id, component, errors }) => {
            return (
              <span key={id}>
                {isEditMode &&
                  project.submission_status === 'Draft' &&
                  !canEditApprovedProjects &&
                  currentTab === 0 && (
                    <CustomAlert
                      type="info"
                      alertClassName="mb-3"
                      content={
                        <Typography className="text-lg leading-5">
                          {project.version === 2
                            ? 'The Identifiers fields cannot be edited anymore. If there was a mistake in completing them, the project needs to be resubmitted with the correct information.'
                            : "After the project's submission, the Identifiers fields cannot be further edited. Any mistake in completing them will require the project to be resubmitted with the correct information."}
                        </Typography>
                      }
                    />
                  )}
                {!!inlineMessage &&
                  (!inlineMessage.tabId || inlineMessage.tabId === id) && (
                    <ProjectsInlineMessage />
                  )}
                {mode === 'edit' &&
                  project?.submission_status === 'Draft' &&
                  warnings.id === parseInt(project_id) &&
                  warnings.warnings.length > 0 && (
                    <CustomAlert
                      type="info"
                      alertClassName="mb-3"
                      content={
                        <Typography className="text-lg leading-5">
                          {warnings.warnings[0]}
                        </Typography>
                      }
                    />
                  )}
                {errors && errors.length > 0 && <ErrorsList {...{ errors }} />}
                {component}
              </span>
            )
          })}
      </div>
    </>
  )
}

export default ProjectsCreate
